import { PropertyHelper } from '@/helpers/property.helper';
import { AddressData } from '@/models/AddressData';
import { AddressResult } from '@/models/AdressResult';
import { AlertService } from '@/services/alert.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Address, Location, PropertiesService, PropertyType, RealProperty, RealPropertyCreate } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { RealPropertyMutable } from '../property-add-full-dialog/property-add-full-dialog.component';

import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    selector: 'app-property-add-address',
    templateUrl: './property-add-address.component.html',
    styleUrls: ['./property-add-address.component.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, NgFor, MatOptionModule, MatInputModule, InputTrimDirective, NgIf, MatAutocompleteModule, MatIconModule]
})
export class PropertyAddAddressComponent implements OnInit, OnDestroy {
  @Input() parent: RealProperty;
  @Input() withAddressSearch = true;
  @Input() withAddressRequired = false;
  @Input() existingProp: RealProperty;
  @Output() propertyCreated = new EventEmitter<RealPropertyCreate>();
  @Output() propertyUpdated = new EventEmitter<RealPropertyMutable>();

  form: UntypedFormGroup;
  formSubmitting = false;

  propertyTypes: PropertyType[];
  propertyTypesName: { [key: string]: string } = {};

  selectedAddress: AddressData;
  public searchAddressResults: AddressData[];

  sub = new Subscription();
  apiAddressError = false;

  constructor(
    private propertiesService: PropertiesService,
    private fb: UntypedFormBuilder,
    public propHelper: PropertyHelper,
    private http: HttpClient,
    private alertService: AlertService
  ) {}

  get form_name() {
    return this.form.get('name') as FormControl<string>;
  }
  get form_type() {
    return this.form.get('type') as FormControl<PropertyType>;
  }
  get form_searchAddress() {
    return this.form.get('searchAddress') as FormControl<string>;
  }

  createAddressSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      return !this.selectedAddress ? { noAddressSelected: true } : null;
    };
  }

  ngOnInit(): void {
    this.propertyTypes = this.parent
      ? PropertyHelper.getPropertyTypesForParent(this.parent.type)
      : PropertyHelper.getPropertyTypes();
    this.propertyTypes.forEach((t) => (this.propertyTypesName[t] = this.propHelper.getTypeName(t)));
    const defaultType =
      this.parent && this.parent.type === PropertyType.CONDOMINIUM ? PropertyType.FLAT : this.propertyTypes[0];
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      type: [defaultType, Validators.required],
      searchAddress: ['', [Validators.minLength(5), this.createAddressSelectedValidator()]]
    });
    if (this.withAddressRequired) {
      this.form_searchAddress.addValidators([Validators.required]);
    }
    if (this.existingProp) {
      this.form_name.setValue(this.existingProp.name);
      this.form_type.setValue(this.existingProp.type);
      this.form_type.disable();
      this.form_searchAddress.setValue(this.propHelper.getAddressLines(this.existingProp.address).join(' '));
      //perform a search address and select the first
      this.doSearchAddress(true);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  doSearchAddress(auto = false) {
    const search = this.form_searchAddress.value as string;
    const showError = true;
    this.selectedAddress = null;
    if (search.length < 5) {
      this.searchAddressResults = null;
      return;
    }
    this.sub = this.http.get('/api-adresse/search/', { params: { q: search } }).subscribe({
      next: (result: AddressResult) => {
        if (auto) {
          //select the first result, show error if no result
          if (result.features.length === 0) {
            this.form_searchAddress.setErrors({ noResult: true });
          } else {
            this.addressSelected(result.features[0]);
          }
        } else {
          this.searchAddressResults = result.features;
        }
        this.apiAddressError = false;
      },
      error: (err: HttpErrorResponse) => {
        this.apiAddressError = true;
      }
    });
  }

  onSubmit() {
    if (this.form.valid) {
      this.formSubmitting = true;
      let address: Address = null;
      let location: Location = null;
      if (this.selectedAddress) {
        location = {
          latitude: this.selectedAddress.geometry.coordinates[1],
          longitude: this.selectedAddress.geometry.coordinates[0]
        };
        address = {
          street: this.selectedAddress.properties.name,
          postalCode: this.selectedAddress.properties.postcode,
          city: this.selectedAddress.properties.city,
          country: 'France'
        };
      }
      if (this.existingProp) {
        //update
        if (address != null) this.existingProp.address = address;
        if (location != null) this.existingProp.location = location;
        this.existingProp.name = this.form_name.value;
        this.propertyUpdated.emit(this.existingProp);
      } else {
        //creation
        const propertyCreate: RealPropertyCreate = {
          name: this.form_name.value,
          type: PropertyType[this.form_type.value]
        };
        if (this.parent) {
          propertyCreate.parentId = this.parent.uid;
        }
        if (this.selectedAddress) {
          propertyCreate.address = address;
          propertyCreate.location = location;
        }
        this.propertyCreated.emit(propertyCreate);
      }
      this.formSubmitting = false;
    }
  }

  displayAddress(address: AddressData) {
    return address && address.properties && address.properties.label ? address.properties.label : '';
  }

  addressSelected(address: AddressData) {
    this.selectedAddress = address;
    this.form_searchAddress.setValue(this.displayAddress(address));
  }
}
